import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { TypeCurveListingRow } from 'types/api-responses';

export const LAUNCHABLE_TYPE_CURVES_KEY = 'launchable-type-curves';

interface TypeCurveHeadersResponse {
  results: TypeCurveListingRow[];
}

/**
 * Type curves a workspace can be launched from.
 *
 * Only needed when the create modal is opened from the listing, where there is
 * no type curve in context. The modal offers the mock's "Target Type Curve
 * Area" picker, and resolves the chosen area to one of these rows — because
 * `POST /queries/inventory_formation_candidates` takes a
 * `typeCurveHeaderDetailsId` and resolves the scenario and area config from it.
 * There is no query that takes an area directly.
 *
 * @param {{ enabled: boolean }} options - Gate the fetch.
 * @return {object} React Query result holding the selectable type curves.
 */
export function useLaunchableTypeCurves(options: { enabled: boolean }) {
  return useQuery<TypeCurveListingRow[], Error>({
    queryKey: [LAUNCHABLE_TYPE_CURVES_KEY],
    queryFn: ({ signal }) =>
      api
        .post('queries/type_curve_headers', {
          json: { query: { page: 1, pageSize: 200 } },
          signal,
        })
        .json<TypeCurveHeadersResponse>()
        .then((response) => response.results ?? []),
    enabled: options.enabled,
    staleTime: 300_000,
  });
}
